/**
 * The steps the booking sheet walks, and where a step sits among them.
 *
 * The sheet and the "In progress" pill on the activity page used to count on
 * their own: the pill had four fixed labels, so a five-step sheet resumed as
 * "Step 4 of 4 · Your details", and a sheet with no package step said "Step 1
 * of 4" while the pill under it said "step 2 of 4" (website test, 2026-11-01).
 * Both read from here now.
 *
 * Step numbers are the sheet's own: 0 package, 1 when, 2 who, 3 details,
 * 4 review. A single package skips 0; a restaurant asks its party on When, so
 * it walks 1, 3, 4.
 */
export type SheetStep = 0 | 1 | 2 | 3 | 4;

export interface SheetStepShape {
  isTableFlow: boolean;
  packageCount: number;
}

export function sheetStepOrder({ isTableFlow, packageCount }: SheetStepShape): readonly SheetStep[] {
  if (isTableFlow) return [1, 3, 4];
  return packageCount <= 1 ? [1, 2, 3, 4] : [0, 1, 2, 3, 4];
}

/**
 * Where a (saved) step sits: 1-based `current` out of `total`, and the step
 * it stands for. A step the flow does not walk lands on the one before it
 * (a restaurant's old "How many" is part of When; a skipped package step is
 * the first step).
 */
export function sheetStepPosition(
  shape: SheetStepShape,
  step: number,
): { current: number; total: number; step: SheetStep } {
  const order = sheetStepOrder(shape);
  const clamped = Math.min(4, Math.max(0, Math.floor(step)));
  let at = 0;
  order.forEach((n, i) => {
    if (n <= clamped) at = i;
  });
  return { current: at + 1, total: order.length, step: order[at]! };
}
